import React, { useEffect, useRef, useState } from "react";
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import { Modal } from 'bootstrap/dist/js/bootstrap';
import { toast } from 'react-toastify';
import { loader } from 'helpers/generic';

const ModalStyled = styled.div`
	background: rgba(0, 0, 0, 0.4);

	&.modal {
		.modal-header h5 {
			font-size: 15px !important;
			line-height: 15px !important;
		}
	}
`;

const FUEL_TYPES = [
	{ id: 1, label: 'GOA' },
	{ id: 5, label: 'SP95' },
];

let axiosCancelToken = null;

const DeliveryNotesAddModal = React.memo((props) => {
	const modalRef = useRef(null);
	const { pcar, closeCallback } = props;

	const [loading, setLoading] = useState(false);
	const [documentNumber, setDocumentNumber] = useState('');
	const [dispatchDate, setDispatchDate] = useState('');
	const [lines, setLines] = useState([{ fuelType: 1, quantity: '' }]);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		const modal = new Modal(modalRef.current, { backdrop: false });

		const hiddenEvent = () => {
			modal._element.removeEventListener('hidden.bs.modal', hiddenEvent);
			modal.dispose();
			closeCallback(false);
			document.body.style.overflow = 'auto';
			document.body.style.paddingRight = '0';
		};

		modal._element.addEventListener('hidden.bs.modal', hiddenEvent);
		modal.show();

		document.body.style.overflow = 'auto';
		document.body.style.paddingRight = '0';

		return function cleanup() {
			axiosCancelToken.cancel();
		};
	}, [closeCallback]);

	const closeModal = (saved = false) => {
		Modal.getInstance(modalRef.current).hide();
		closeCallback(saved);
	};

	const addLine = () => {
		setLines(prev => [...prev, { fuelType: 1, quantity: '' }]);
	};

	const removeLine = (idx) => {
		setLines(prev => prev.filter((_, i) => i !== idx));
	};

	const updateLine = (idx, field, value) => {
		setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
	};

	const handleSave = async () => {
		if (!documentNumber.trim()) {
			toast.error('El nº de albarán es obligatorio');
			return;
		}
		if (!dispatchDate) {
			toast.error('La fecha de entrega es obligatoria');
			return;
		}
		if (lines.some(l => !l.quantity || parseInt(l.quantity) <= 0)) {
			toast.error('Todas las líneas deben tener una cantidad válida');
			return;
		}

		setLoading(true);
		await axios.post(`/api/tankertrucks-delivery-notes/add-manual/${pcar}`, [{
			documentNumber,
			dispatchDate: moment(dispatchDate).format('DD/MM/YYYY HH:mm:ss'),
			lines: lines.map(l => ({ fuelType: parseInt(l.fuelType), quantity: parseInt(l.quantity) })),
		}], {
			cancelToken: axiosCancelToken.token
		}).then(() => {
			toast.success('Albarán añadido correctamente');
			closeModal(true);
		}).catch((error) => {
			if (axios.isCancel(error)) return;
			toast.error(error.response?.data?.message ?? 'Ha ocurrido un error al guardar el albarán');
		});
		setLoading(false);
	};

	return (
		<ModalStyled className="modal" tabIndex="-1" ref={modalRef}>
			{loading && loader}
			<div className="modal-dialog modal-md modal-dialog-centered">
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">Añadir albarán manual</h5>
						<button type="button" className="btn-close" onClick={() => closeModal(false)}></button>
					</div>
					<div className="modal-body">
						<div className="mb-3">
							<label className="form-label fw-bold" style={{ fontSize: 12 }}>Nº de albarán <span className="text-danger">*</span></label>
							<input
								type="text"
								className="form-control form-control-sm"
								value={documentNumber}
								onChange={e => setDocumentNumber(e.target.value)}
								placeholder="Ej. 2026-05-12/001"
							/>
						</div>
						<div className="mb-3">
							<label className="form-label fw-bold" style={{ fontSize: 12 }}>Fecha de entrega <span className="text-danger">*</span></label>
							<input
								type="datetime-local"
								className="form-control form-control-sm"
								value={dispatchDate}
								onChange={e => setDispatchDate(e.target.value)}
							/>
						</div>
						<div className="mb-2">
							<div className="d-flex align-items-center justify-content-between mb-2">
								<label className="form-label fw-bold mb-0" style={{ fontSize: 12 }}>Líneas <span className="text-danger">*</span></label>
								<button
									type="button"
									className="btn btn-sm btn-plenoil btn-plenoil-outline-blue px-2 py-0"
									style={{ fontSize: 11, minHeight: 26 }}
									onClick={addLine}
								>
									<i className="bi bi-plus"></i> Añadir línea
								</button>
							</div>
							<table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
								<thead>
									<tr>
										<th>Tipo combustible</th>
										<th>Cantidad (L)</th>
										<th style={{ width: 36 }}></th>
									</tr>
								</thead>
								<tbody>
									{lines.map((line, idx) => (
										<tr key={idx}>
											<td>
												<select
													className="form-select form-select-sm"
													value={line.fuelType}
													onChange={e => updateLine(idx, 'fuelType', e.target.value)}
												>
													{FUEL_TYPES.map(ft => (
														<option key={ft.id} value={ft.id}>{ft.label}</option>
													))}
												</select>
											</td>
											<td>
												<input
													type="number"
													className="form-control form-control-sm no-arrows"
													value={line.quantity}
													min="1"
													onChange={e => updateLine(idx, 'quantity', e.target.value)}
												/>
											</td>
											<td className="text-center align-middle">
												{lines.length > 1 && (
													<button
														type="button"
														className="btn btn-sm p-0"
														style={{ color: 'var(--bs-red)', lineHeight: 1 }}
														onClick={() => removeLine(idx)}
													>
														<i className="bi bi-trash"></i>
													</button>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
					<div className="modal-footer gap-2 justify-content-end">
						<button
							type="button"
							className="btn btn-sm btn-plenoil btn-plenoil-outline-blue"
							style={{ width: 'max-content' }}
							onClick={() => closeModal(false)}
						>
							Cancelar
						</button>
						<button
							type="button"
							className="btn btn-sm btn-plenoil btn-plenoil-orange d-flex flex-row flex-nowrap gap-1 align-items-center justify-content-center"
							style={{ width: 'max-content' }}
							onClick={handleSave}
							disabled={loading}
						>
							{loading
								? <><span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10, borderWidth: 2 }}></span> Guardando...</>
								: 'Guardar'
							}
						</button>
					</div>
				</div>
			</div>
		</ModalStyled>
	);
}, () => true);

export default DeliveryNotesAddModal;
